/**
 * Photo Upload Utilities for time4subs
 * Uploads directly to R2 via Cloudflare Worker
 */

import { validateImageFile } from "./fileValidation";
import { PHOTO_UPLOAD_URL } from "./apiConfig";

/**
 * Capture photo using device camera
 * @param {string} facing - 'user' for front camera (selfies), 'environment' for back camera (job photos)
 * @returns {Promise<File>} Captured photo file
 */
export function capturePhoto(facing = "environment") {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = facing;

    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        // Validate file type and size
        const validation = await validateImageFile(file, {
          maxSize: 20 * 1024 * 1024,
        });
        if (!validation.valid) {
          reject(new Error(validation.error));
          return;
        }
        resolve(file);
      } else {
        reject(new Error("No photo captured"));
      }
    };

    // Handle cancel
    input.oncancel = () => reject(new Error("Cancelled"));

    input.click();
  });
}

/**
 * Compress an image file/blob to a maximum dimension and quality
 * @param {File|Blob} file - Original image file (or blob)
 * @param {number|object} maxWidth - Maximum size (in pixels) of the longest edge.
 *   Also accepts an options object { maxWidth, maxHeight, quality } for callers
 *   that pass one by mistake, so a positional/object mismatch can't silently no-op.
 * @param {number} quality - JPEG quality (0.0 to 1.0)
 * @returns {Promise<File>} Compressed image file
 */
export function compressImage(file, maxWidth = 1920, quality = 0.8) {
  // Normalize an accidental options-object second argument, e.g.
  // compressImage(file, { maxWidth: 1920, maxHeight: 1920, quality: 0.8 })
  let maxDim = maxWidth;
  let q = quality;
  if (maxWidth && typeof maxWidth === "object") {
    maxDim = maxWidth.maxWidth ?? maxWidth.maxHeight ?? 1920;
    q = maxWidth.quality ?? quality;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error("Failed to read file"));

    reader.onload = (e) => {
      const img = new Image();

      img.onerror = () => reject(new Error("Failed to load image"));

      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Resize based on the longest edge, so portrait photos (whose width
        // is the short edge) get scaled down too, not just landscape ones.
        const longestEdge = Math.max(width, height);
        if (longestEdge > maxDim) {
          const scale = maxDim / longestEdge;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to compress image"));
              return;
            }
            resolve(
              new File([blob], file.name || "photo.jpg", {
                type: "image/jpeg",
                lastModified: Date.now(),
              }),
            );
          },
          "image/jpeg",
          q,
        );
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Upload blob directly to R2 via Cloudflare Worker
 * @param {Blob} blob - Image blob to upload
 * @param {string} key - Full path/key for R2
 * @returns {Promise<{key: string, bucket: string}>} Upload result
 */
async function uploadToWorker(blob, key) {
  const response = await fetch(
    `${PHOTO_UPLOAD_URL}?key=${encodeURIComponent(key)}`,
    {
      method: "PUT",
      body: blob,
      headers: { "Content-Type": "image/jpeg" },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Upload failed");
  }

  const result = await response.json();
  return { key: result.key, bucket: "time4subs-photos" };
}

/**
 * Upload clock photo directly to R2 via Cloudflare Worker
 * @param {File|Blob} file - Photo file to upload
 * @param {string} orgId - Organization UUID
 * @param {string} employeeId - Employee UUID
 * @param {string} action - 'in' or 'out'
 * @returns {Promise<string>} R2 file key
 */
async function uploadClockPhotoToR2(file, orgId, employeeId, action) {
  const compressed = await compressImage(file, 1920, 0.8);

  // Path: clock-photos/{orgId}/clock-{action}/{date}/{employeeId}/{timestamp}.jpg
  const date = new Date().toISOString().split("T")[0];
  const key = `clock-photos/${orgId}/clock-${action}/${date}/${employeeId}/${Date.now()}.jpg`;

  const result = await uploadToWorker(compressed, key);
  return result.key;
}

/**
 * Upload job photo directly to R2 via Cloudflare Worker
 * @param {File|Blob} file - Photo file to upload
 * @param {string} orgId - Organization UUID
 * @param {string} jobId - Job UUID
 * @returns {Promise<{key: string, bucket: string}>} Upload result
 */
async function uploadJobPhotoToR2(file, orgId, jobId) {
  const compressed = await compressImage(file, 1920, 0.8);

  // Path: job-photos/{orgId}/{jobId}/{date}/{timestamp}.jpg
  const date = new Date().toISOString().split("T")[0];
  const timestamp = Date.now();
  const key = `job-photos/${orgId}/${jobId}/${date}/${timestamp}.jpg`;

  const result = await uploadToWorker(compressed, key);
  return result;
}

/**
 * Capture and upload a clock in/out photo
 * @param {string} jobId - Job UUID
 * @param {string} employeeId - Employee UUID
 * @param {string} action - 'in' or 'out'
 * @param {string} orgId - Organization UUID (optional, for R2 path)
 * @returns {Promise<{url: string|null, location: object|null}>}
 */
export async function captureClockPhoto(
  jobId,
  employeeId,
  action,
  orgId = null,
) {
  // Capture photo IMMEDIATELY - must be first to preserve user gesture on iOS Safari
  const file = await capturePhoto("environment");

  // Now get GPS (can be async, photo is already captured)
  let location = null;
  try {
    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
      });
    });
    location = {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
    };
  } catch (e) {
    console.warn("Could not get location:", e);
  }

  // Upload to time4subs-photos bucket
  try {
    const org = orgId || "11111111-1111-1111-1111-111111111111";
    const filePath = await uploadClockPhotoToR2(file, org, employeeId, action);
    return { url: filePath, location };
  } catch (e) {
    console.warn("Clock photo upload failed:", e);
    return { url: null, location };
  }
}

/**
 * Capture and upload a job site photo
 * @param {string} jobId - Job UUID
 * @param {string} employeeId - Employee UUID who took the photo
 * @param {string} orgId - Organization UUID (optional, for R2 path)
 * @returns {Promise<{url: string, bucket: string, location: object|null}>}
 */
export async function captureJobPhoto(jobId, employeeId, orgId = null) {
  // Capture photo IMMEDIATELY - must be first to preserve user gesture on iOS Safari
  const file = await capturePhoto("environment");

  // Now get GPS (can be async, photo is already captured)
  let location = null;
  try {
    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
      });
    });
    location = {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
    };
  } catch (e) {
    console.warn("Could not get location:", e);
  }

  // Upload to time4subs-photos bucket
  const org = orgId || "11111111-1111-1111-1111-111111111111";
  const result = await uploadJobPhotoToR2(file, org, jobId);

  return { url: result.key, bucket: result.bucket, location };
}
